export const COOKIES = 'cookies';
export const SESSION_STORAGE = 'session-storage';
export const LOCAL_STORAGE = 'local-storage';

/**
 * check if storage type is enabled
 * storageType - 'local-storage', 'session-storage', or 'cookies'
 */
export const isStorageEnabled = (storageType: string) => {

    if(storageType === COOKIES) return true;

    if(storageType === LOCAL_STORAGE){

        const temp = 'test';

        try {
            window.localStorage.setItem(temp, temp);
            window.localStorage.removeItem(temp);
            return true;
        }
        catch(e) {
            return false;
        }
    }

    if(storageType === SESSION_STORAGE){

        const temp = 'test';

        try {
            window.sessionStorage.setItem(temp, temp);
            window.sessionStorage.removeItem(temp);
            return true;
        }
        catch(e) {
            return false;
        }
    }

    return false;
};

/**
 * set cookie
 * expiration - cookie expiration in minutes (-1 = cookie expires when browser is closed)
 * samesite - strict or lax
 */
export const setCookie = (
    name: string,
    value: any,
    expiration: number = -1,
    secure: boolean = false,
    samesite: string|undefined = undefined,
    preventStringify: boolean = false,
    domain?: string, // .waves.com
) => {

    const escapedValue = encodeURIComponent(preventStringify ? value : JSON.stringify(value));
    const exp = Number(expiration) || -1;
    let cookieString = '';

    const domainValue = domain ? `domain=${ domain };` : '';

    if(exp === -1) {

        // cookie expires when browser is closed
        // If neither expires nor max-age specified the cookie will expire at the end of session.
        cookieString = `${ name }=${ escapedValue };path=/;${ domainValue }`;
    }
    else{
        const date = new Date();
        date.setMinutes(date.getMinutes() + expiration);

        // option to set expiration in days:
        //864e5 = 86400000 = 1000*60*60*24 represents the number of milliseconds in a 24 hour day.
        //date.setTime(date.getTime() + (days * 864e5));

        const expires = date.toUTCString();
        cookieString = `${ name }=${ escapedValue };path=/;${ domainValue }expires=${ expires }`;
    }

    if(samesite){
        // The strict value will prevent the cookie from being sent by the browser to the target site in all cross-site browsing context, even when following a regular link.
        // The lax value will only send cookies for TOP LEVEL navigation GET requests. This is sufficient for user tracking, but it will prevent many CSRF attacks.
        cookieString += `;samesite=${samesite}`;
    }

    if(secure){
        // Cookie to only be transmitted over secure protocol as https. Before Chrome 52, this flag could appear with cookies from http domains.
        cookieString += ';secure';
    }

    document.cookie = cookieString;
};

/**
 * Get cookie by name
 */
export const getCookie = (name: string) => {

    const cookies = document.cookie.split(';');

    for (let i=0; i<cookies.length; i++){

        const cookie = cookies[i];
        const index = cookie.indexOf('=');
        const key = cookie.substr(0, index);
        const value = cookie.substr(index + 1);

        if(key.trim().toLowerCase() === name.trim().toLowerCase()){
            return decodeURIComponent(value);
        }
    }

    return null;
};

/**
 * save a value to the local storage
 */
export const setLocalStorageItem = (storageName: string, value: any) => {
    const escapedValue = encodeURIComponent(JSON.stringify(value));
    window.localStorage.setItem(storageName, escapedValue);
};

/**
 * get a value to the local storage
 */
export const getLocalStorageItem = (storageName: string) => {
    const value = window.localStorage.getItem(storageName) as string;
    return decodeURIComponent(value);
};

/**
 * save a value to the session storage
 */
export const setSessionStorageItem = (storageName: string, value: any) => {
    const escapedValue = encodeURIComponent(JSON.stringify(value));
    window.sessionStorage.setItem(storageName, escapedValue);
};

/**
 * get a value to the session storage
 */
export const getSessionStorageItem = (storageName: string) => {
    const value = window.sessionStorage.getItem(storageName) as string;
    return decodeURIComponent(value);
};

/**
 * save to web storage or cookies
 * storageType - 'local-storage', 'session-storage', or 'cookies'
 * cookiesExpiration - cookies expiration in minutes (-1 = cookies expire when browser is closed)
 */
export const saveToStorage = (storageType: string, storageName: any, value: any, cookiesExpiration: number = -1) => {

    // if this storage type is not supported -> do nothing
    if(!isStorageEnabled(storageType)) return;

    if(storageType === COOKIES) {
        setCookie(storageName, value, cookiesExpiration);
    }

    if(storageType === LOCAL_STORAGE) {
        setLocalStorageItem(storageName, value);
    }

    if(storageType === SESSION_STORAGE) {
        setSessionStorageItem(storageName, value);
    }
};

/**
 * get value from storage
 * storageType - 'local-storage', 'session-storage', or 'cookies'
 */
export const getFromStorage = (storageType: string, storageName: string) : string|null => {

    // if this storage type is not supported -> do nothing
    if(!isStorageEnabled(storageType)) return null;

    if(storageType === COOKIES) {
        return getCookie(storageName);
    }

    if(storageType === LOCAL_STORAGE) {
        return getLocalStorageItem(storageName);
    }

    if(storageType === SESSION_STORAGE) {
        return getSessionStorageItem(storageName);
    }

    return null;
};

/**
 * if storage already exists, append to it, otherwise create a new one
 * storageType - 'local-storage', 'session-storage', or 'cookies'
 * storageName - it is used like key name in web storage, or like cookie name
 * this is a property name that is used to store the value inside the general widgets object
 * value - the value that should be stored
 * cookiesExpiration - cookies expiration in minutes (-1 = cookies expire when browser is closed)
 */
export const appendToStorage = (storageType: string, storageName: string, key: string, value: any, cookiesExpiration: number = -1) => {

    let data = getFromStorage(storageType, storageName) as any;

    try{
        data = JSON.parse(data);
    }
    catch(err){}

    if(!data) {

        // create new entry
        data = {
            [key]: value
        };
    }
    else{
        // append the entry
        data[key] = value;
    }

    // overwrite the storage
    saveToStorage(storageType, storageName, data, cookiesExpiration = -1);
};